// Small display helpers shared by the dashboards and the lesson page.

export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function isPast(value: string | null | undefined): boolean {
  return !!value && new Date(value).getTime() < Date.now();
}

// <input type="datetime-local"> works in local time without a zone; the API wants ISO with a zone.
export function toDateTimeLocalValue(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function fromDateTimeLocalValue(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

export interface SubmissionWindow {
  submissions_enabled: boolean;
  submission_deadline: string | null;
}

// Whether a student can still submit for an assignment, and a short label for it. The backend enforces the
// same rules (app/api/submissions.py); this only explains them up front.
export function describeSubmissionWindow(assignment: SubmissionWindow | null | undefined): { open: boolean; label: string | null } {
  if (!assignment) {
    return { open: true, label: null };
  }
  if (!assignment.submissions_enabled) {
    return { open: false, label: 'Submissions closed' };
  }
  if (assignment.submission_deadline) {
    if (isPast(assignment.submission_deadline)) {
      return { open: false, label: `Deadline passed (${formatDateTime(assignment.submission_deadline)})` };
    }
    return { open: true, label: `Due ${formatDateTime(assignment.submission_deadline)}` };
  }
  return { open: true, label: null };
}

// A day, for plan periods: "Apr 25, 2027".
export function formatDate(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '';
}

// "8 min", "45 s": simulation time left.
export function formatMinutes(seconds: number): string {
  if (seconds < 60) {
    return `${Math.max(0, Math.floor(seconds))} s`;
  }
  return `${Math.floor(seconds / 60)} min`;
}

// A daily allowance in words: "10 minutes", "1 hour", "2 hours".
export function formatDailyTime(seconds: number): string {
  if (seconds >= 3600 && seconds % 3600 === 0) {
    return seconds === 3600 ? '1 hour' : `${seconds / 3600} hours`;
  }
  const minutes = Math.round(seconds / 60);
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}

// When today's simulation time comes back (midnight UTC), in the viewer's time: "at 02:00" or "tomorrow at 01:00".
export function formatResetTime(resetsAt: string): string {
  const resets = new Date(resetsAt);
  const time = resets.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return resets.toDateString() === new Date().toDateString() ? `at ${time}` : `tomorrow at ${time}`;
}

// "3 days ago", "in 2 hours": how long ago a moment was, for lists (the exact time goes in a tooltip, formatUtc).
export function formatRelativeTime(value: string, now: number = Date.now()): string {
  const seconds = (new Date(value).getTime() - now) / 1000;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]];
  const format = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) {
      return format.format(Math.trunc(seconds / size), unit);
    }
  }
  return 'just now';
}

// "2026-11-02 14:03 UTC": an exact moment, in UTC (the app's days are UTC days).
export function formatUtc(value: string): string {
  return `${new Date(value).toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}
